import React from 'react';
import { motion } from 'framer-motion';
import {
  CpuChipIcon,
  GlobeAltIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import Logo from '../assets/Fuat (1).svg';

/* ─── Data Constants ─── */

const architecture = `CLI Command
  |
  v
FileDiscovery ---- .uiqignore, symlinks, deterministic ordering
  |
  v
AnalyzerOrchestrator ---- dependency graph, parallel execution
  |
  v
20 Analyzers (Core + Framework + Backend + Quality)
  |
  v
Normalizer ---- canonical output format, deterministic serialization
  |
  v
CacheLayer ---- SHA-256 hashing, analyzer versioning
  |
  v
IntelligenceIndexer ---- pattern merging, confidence scoring
  |
  v
InsightEngine ---- hubs, orphans, cycles, chains, smells
  |
  v
QueryEngine ---- inverted index, synonym/fuzzy matching
  |
  v
Generate/Export ---- CLAUDE.md, AGENTS.md, .cursorrules, etc.
  |
  v
MCP Server ---- stdio/HTTP transport, real-time AI integration`;

const techStack = [
  { name: 'TypeScript', desc: 'Strict mode, branded types, full type safety' },
  { name: 'ts-morph', desc: 'AST analysis for accurate component detection' },
  { name: 'tsup', desc: 'Dual ESM + CJS builds' },
  { name: 'Commander', desc: 'CLI framework with 13 commands' },
  { name: 'Chokidar', desc: 'File watching for incremental analysis' },
  { name: 'Node.js >= 18', desc: 'Zero external runtime dependencies' },
];

const projectStats = [
  { label: 'Source files', value: '96' },
  { label: 'Test files', value: '36' },
  { label: 'Passing tests', value: '500+' },
  { label: 'Analyzers', value: '20' },
  { label: 'CLI commands', value: '13' },
  { label: 'AI targets', value: '7' },
];

const analyzerCategories = [
  {
    title: 'Core',
    count: 7,
    icon: CpuChipIcon,
    color: '#FF3366',
    items: ['Import', 'Component', 'Styling', 'FileStructure', 'Dependency', 'Structure', 'UX'],
  },
  {
    title: 'Framework',
    count: 7,
    icon: GlobeAltIcon,
    color: '#FF6B4D',
    items: ['Next.js', 'Nuxt', 'SvelteKit', 'Angular', 'Solid', 'Lit', 'Qwik'],
  },
  {
    title: 'Backend',
    count: 4,
    icon: CircleStackIcon,
    color: '#FF3366',
    items: ['API Routes', 'Database', 'Auth', 'Env Config'],
  },
  {
    title: 'Quality',
    count: 2,
    icon: ShieldCheckIcon,
    color: '#FF6B4D',
    items: ['Coverage', 'Performance'],
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5 },
  }),
};

/* ─── Gradient Border Box ─── */
const GradientBox = ({ children, className = '' }) => (
  <div className={`relative rounded-2xl p-px bg-gradient-to-br from-[#FF3366]/40 via-[#FF6B4D]/20 to-[#FF3366]/10 ${className}`}>
    <div className="rounded-2xl bg-[#0A0A10] p-8 md:p-12">
      {children}
    </div>
  </div>
);

/* ─── Reusable Terminal Shell ─── */
const TerminalBlock = ({ title, children, className = '' }) => (
  <div className={`rounded-2xl border border-white/10 bg-[#0D0D12] overflow-hidden ${className}`}>
    <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/5 bg-[#0A0A0F]">
      <div className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#FFBD2E]" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
      {title && <span className="ml-2 text-xs text-[#B4A5BD] font-mono">{title}</span>}
    </div>
    <div className="p-5 font-mono text-sm leading-relaxed">{children}</div>
  </div>
);

/* ─── Page ─── */
export default function About() {
  return (
    <div className="relative overflow-hidden">
      <SEO
        title="About - UIQuarter"
        description="Learn about UIQuarter's architecture, tech stack, and design philosophy."
      />

      {/* Background glow effects */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF3366]/5 rounded-full blur-3xl" />
      <div className="absolute top-40 right-1/4 w-96 h-96 bg-[#FF6B4D]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/3 left-1/3 w-80 h-80 bg-[#FF3366]/3 rounded-full blur-3xl" />

      {/* ── 1. Hero ── */}
      <section className="relative pt-20 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#FF3366]/30 bg-[#FF3366]/5 mb-6">
              <BoltIcon className="w-4 h-4 text-[#FF3366]" />
              <span className="text-sm text-[#B4A5BD]">Static analysis for UI codebases</span>
            </div>

            <div className="flex justify-center mb-8">
              <img
                src={Logo}
                alt="UIQuarter"
                className="w-28 h-28"
              />
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              About{' '}
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                UIQuarter
              </span>
            </h1>
            <p className="text-xl text-[#B4A5BD] max-w-3xl mx-auto mb-6">
              UIQuarter is a static analysis CLI that understands UI codebases. It extracts component patterns,
              maps dependency relationships, detects architectural issues, and generates optimized context for AI coding tools.
            </p>
            <p className="text-lg text-[#B4A5BD]/80 max-w-2xl mx-auto">
              One command replaces hours of manual documentation. Every AI assistant instantly
              knows your component structure, naming conventions, dependency graph, and styling patterns.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── 2. Stats Grid ── */}
      <section className="py-8 border-y border-white/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {projectStats.map((stat, i) => (
              <motion.div
                key={stat.label}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-[#B4A5BD] mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3. Why UIQuarter ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 text-center">
              Why{' '}
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                UIQuarter?
              </span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8 mt-10">
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF3366]/10 flex items-center justify-center">
                  <BoltIcon className="w-5 h-5 text-[#FF3366]" />
                </div>
                <h3 className="text-lg font-semibold">Context Gap</h3>
                <p className="text-[#B4A5BD] text-sm leading-relaxed">
                  AI coding assistants work best when they understand your project's architecture.
                  Without context, they generate code that doesn't match your patterns, uses wrong
                  imports, and misses existing components.
                </p>
              </div>
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF6B4D]/10 flex items-center justify-center">
                  <CpuChipIcon className="w-5 h-5 text-[#FF6B4D]" />
                </div>
                <h3 className="text-lg font-semibold">One Command</h3>
                <p className="text-[#B4A5BD] text-sm leading-relaxed">
                  UIQuarter solves this by analyzing your codebase and generating structured context
                  files that each AI tool can read. Run one command and every assistant instantly knows
                  your architecture.
                </p>
              </div>
              <div className="space-y-3">
                <div className="w-10 h-10 rounded-xl bg-[#FF3366]/10 flex items-center justify-center">
                  <ShieldCheckIcon className="w-5 h-5 text-[#FF3366]" />
                </div>
                <h3 className="text-lg font-semibold">Architecture Guardian</h3>
                <p className="text-[#B4A5BD] text-sm leading-relaxed">
                  Beyond AI context, UIQuarter serves as a continuous architecture guardian --
                  detecting drift between snapshots, enforcing conventions in CI, and surfacing
                  architectural smells before they become problems.
                </p>
              </div>
            </div>
          </motion.div>
        </GradientBox>
      </section>

      {/* ── 4. Architecture Pipeline ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              Architecture{' '}
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                Pipeline
              </span>
            </h2>
            <p className="text-[#B4A5BD] text-lg max-w-2xl mx-auto">
              From CLI invocation to AI-ready output -- every step is deterministic, cacheable, and fast.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <TerminalBlock title="pipeline">
              <pre className="text-xs sm:text-sm text-[#B4A5BD] overflow-x-auto whitespace-pre leading-relaxed">
                {architecture}
              </pre>
            </TerminalBlock>
          </motion.div>
        </div>
      </section>

      {/* ── 5. Analyzer Categories ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">20</span>{' '}
              Deep Analyzers
            </h2>
            <p className="text-[#B4A5BD] text-lg max-w-2xl mx-auto">
              Multi-framework pattern detection across your entire stack. Components, styling, accessibility, backend routes -- nothing is missed.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {analyzerCategories.map((cat, i) => (
              <motion.div
                key={cat.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                className="rounded-xl border border-white/5 bg-white/[0.02] p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${cat.color}15` }}
                  >
                    <cat.icon className="w-5 h-5" style={{ color: cat.color }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{cat.title}</h3>
                  </div>
                  <span
                    className="ml-auto text-xs font-bold font-mono px-2.5 py-1 rounded-full"
                    style={{
                      backgroundColor: `${cat.color}15`,
                      color: cat.color,
                    }}
                  >
                    {cat.count}
                  </span>
                </div>
                <ul className="space-y-2">
                  {cat.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-[#B4A5BD]">
                      <CheckCircleIcon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: `${cat.color}80` }} />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </GradientBox>
      </section>

      {/* ── 6. Tech Stack ── */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold mb-4">
              Tech{' '}
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                Stack
              </span>
            </h2>
            <p className="text-[#B4A5BD] text-lg max-w-2xl mx-auto">
              Built with modern tooling. Zero external runtime dependencies.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {techStack.map((tech, i) => (
              <motion.div
                key={tech.name}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={cardVariants}
                className="relative rounded-2xl p-px bg-gradient-to-br from-[#FF3366]/20 via-[#FF6B4D]/10 to-transparent"
              >
                <div className="rounded-2xl bg-[#0A0A10] p-5 h-full">
                  <div className="font-mono text-sm font-semibold bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent mb-2">
                    {tech.name}
                  </div>
                  <p className="text-sm text-[#B4A5BD] leading-relaxed">{tech.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. Open Source CTA ── */}
      <section className="py-20 px-4">
        <GradientBox className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-4">
              Open{' '}
              <span className="bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] bg-clip-text text-transparent">
                Source
              </span>
            </h2>
            <p className="text-[#B4A5BD] text-lg max-w-xl mx-auto mb-8">
              UIQuarter is released under the MIT License. Contributions, issues, and feedback are welcome.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://github.com/fuatkeles/uiquarter"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-gradient-to-r from-[#FF3366] to-[#FF6B4D] text-white font-semibold hover:opacity-90 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                View on GitHub
              </a>
              <a
                href="https://www.npmjs.com/package/uiquarter"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl border border-white/10 text-white font-semibold hover:bg-white/5 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M1.763 0C.786 0 0 .786 0 1.763v20.474C0 23.214.786 24 1.763 24h20.474c.977 0 1.763-.786 1.763-1.763V1.763C24 .786 23.214 0 22.237 0zM5.13 5.323l13.837.019-.009 13.836h-3.464l.01-10.382h-3.456L12.04 19.17H5.113z" />
                </svg>
                npm Package
              </a>
            </div>

            <div className="mt-10 inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-[#FF3366]/60 to-[#FF6B4D]/30 p-px">
              <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-[#0D0D12] font-mono text-sm">
                <span className="text-[#FF3366]">$</span>
                <span className="text-white">npm install -g uiquarter</span>
                <button
                  onClick={() => navigator.clipboard.writeText('npm install -g uiquarter')}
                  className="ml-2 text-[#B4A5BD] hover:text-white transition-colors"
                  title="Copy"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <rect x="9" y="9" width="13" height="13" rx="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                </button>
              </div>
            </div>
          </motion.div>
        </GradientBox>
      </section>
    </div>
  );
}
